import { Button, Card, Descriptions, Message, Modal, Space, Tabs, Table, Tag } from "@arco-design/web-react"
import { BillAPI, VendorAPI } from "@/apis/purchasing";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Number, Currency } from "@/components/Numeric";
import { EnumLabel } from "@/components/Enum";
import { BillStatusDef } from "@/enums/purchasing";
import { TermsDef } from "@/enums/common";
import { FileUtil } from "@/utils";
import GLImpactButton from "@/components/GLImpactButton";
import SendEmailButton from "@/components/SendEmailButton";
import Reference from "@/components/Reference";

const TabPane = Tabs.TabPane;

const PaymentLink = ({ billId, children }) => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState();

    const getPayments = async () => {
        const values = await BillAPI.payments(billId);
        setData(values);
    }

    return (
        <>
            <Button type="text" onClick={() => setVisible(true)}>{children}</Button>
            <Modal visible={visible}
                style={{ width: 650 }}
                afterOpen={() => getPayments()}
                onOk={() => setVisible(false)}
                onCancel={() => setVisible(false)}
                title="Payments"
            >
                <Table rowKey="id" columns={[{
                    title: "Account Name",
                    dataIndex: "accountName"
                }, {
                    title: "Payment Date",
                    dataIndex: "date"
                }, {
                    title: "Payment Amount",
                    render: (col, record) => <Currency value={record.amount * -1} />
                }]}
                    data={data} />
            </Modal>
        </>
    )
}

const BillKeyLink = ({ children }) => {
    const [visible, setVisible] = useState(false);
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billKey, setBillKey] = useState();

    useEffect(() => {
        const fetchBills = async () => {
            if (!billKey) return;

            try {
                const result = await BillAPI.query({ billKey });
                setBills(result.records);
            } catch (error) {
                console.error('Failed to fetch bills:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBills();
    }, [billKey]);

    useEffect(() => {
        if (children) {
            setBillKey(children);
        }
    }, [children]);

    const showModal = () => {
        setVisible(true);
    };

    const handleOk = () => {
        setVisible(false);
    };

    const handleCancel = () => {
        setVisible(false);
    };

    return (
        <div>
            <a href='###' onClick={showModal}>{billKey}</a>
            <Modal
                title={`Bills with key ${billKey}`}
                visible={visible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <Table rowKey="id" columns={[
                        {
                            title: "Bill#",
                            dataIndex: "billNo",
                            render: (col, record) => (
                                <Link to={`/workspace/purchasing/bills/${record.id}`} onClick={handleCancel}>
                                    {record.billNo}
                                </Link>
                            )
                        },
                        {
                            title: "Vendor",
                            dataIndex: "vendorName"
                        },
                        {
                            title: "Amount",
                            render: (col, record) => <Currency value={record.totalAmount} />
                        },
                        {
                            title: "Status",
                            render: (col, record) => <EnumLabel enumObj={BillStatusDef} value={record.status} />
                        }
                    ]} data={bills} />
                )}
            </Modal>
        </div>
    );
};

export const BillInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [bill, setBill] = useState({});
    const [printLoading, setPrintLoading] = useState(false);

    const getInfo = useCallback(async () => {
        const values = await BillAPI.get(params.billId);
        setBill(values);
    }, [params]);

    const back = () => {
        navigate("/workspace/purchasing/bills");
    }

    const deleteBill = async () => {
        await BillAPI.delete(params.billId);
        Message.success("Bill voided successfully");
        back();
    }

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    return (
        <Card title={<Space>
            <span>{`Bill #` + bill.billNo}</span>
            <Tag><EnumLabel enumObj={BillStatusDef} value={bill.status} /></Tag>
        </Space>} extra={
            <Space>
                {bill.status < 6 &&
                    <Link to={`/workspace/purchasing/bills/${params.billId}/edit`}>
                        <Button type="primary">Edit</Button>
                    </Link>
                }
                {bill.status < 6 && bill.paidAmount <= 0 &&
                    <Button type="primary" status="danger" onClick={() => deleteBill()}>Void Bill</Button>
                }
                {bill.status < 6 &&
                    <GLImpactButton type="2" from={params.billId} />
                }
                {bill.status !== 6 &&
                    <Button type="primary" onClick={async () => {
                        setPrintLoading(true);
                        try {
                            const res = await BillAPI.print(bill.id);
                            FileUtil.download(res.data, `${bill.billNo}.pdf`);
                        } catch (error) {
                            Message.error(error.message);
                        } finally {
                            setPrintLoading(false);
                        }
                    }} loading={printLoading}>Print</Button>
                }
                {bill.status !== 6 &&
                    <SendEmailButton getEmailContent={async () => {
                        return await BillAPI.getEmail(bill.id);
                    }} getToList={async () => {
                        const vendor = await VendorAPI.get(bill.vendorId);
                        return vendor.contacts;
                    }} sendEmail={async (val) => {
                        await BillAPI.sendEmail(bill.id, val);
                    }} />
                }
                <Button onClick={() => back()}>Back</Button>
            </Space>}>
            <Descriptions colon=":" layout="inline-horizontal" data={[
                { label: "Vendor", value: bill.vendorName },
                { label: "Bill Date", value: bill.billDate },
                { label: "Paid Date", value: bill.processDate },
                { label: "Address", value: bill.vendorAddress },
                { label: "Item Receipt#", value: <Link to={`/workspace/purchasing/receipt/${bill.receiptId}`}>{bill.receiptNo}</Link> },
                { label: "PO#", value: <Link to={`/workspace/purchasing/po/${bill.poId}`}>{bill.poNo}</Link> },
                { label: "Invoice#", value: bill.invoiceNo },
                { label: "Terms", value: <EnumLabel enumObj={TermsDef} value={bill.term} /> },
                { label: "Reference#", value: <Reference value={bill.reference} /> },
                { label: "Due Date", value: bill.dueDate },
                { label: "Total Amount", value: <Currency value={bill.totalAmount} /> },
                {
                    label: "Payment Amount",
                    value: <PaymentLink billId={bill.id}>
                        <Currency value={bill.paidAmount} />
                    </PaymentLink>
                },
                { label: "Balance Due", value: <Currency value={bill.totalAmount - bill.paidAmount} /> },
                { label: "Memo", value: bill.memo },
                { label: "Key", value: <BillKeyLink>{bill.billKey}</BillKeyLink> },
            ]} />

            <Tabs defaultActiveTab="2" type="card-gutter">
                <TabPane key="1" title={
                    <Space size={80}>
                        <span>Expenses</span>
                        <span><Currency value={bill.totalExpense} /></span>
                    </Space>
                }>

                    <Table rowKey="id" columns={[
                        { title: "Account", dataIndex: "accountName" },
                        { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
                        { title: "Memo", dataIndex: "memo" },
                    ]} data={bill.expenses || []} />
                </TabPane>
                <TabPane key="2" title={
                    <Space size={80}>
                        <span>Items</span>
                        <span><Currency value={bill.totalItems} /></span>
                    </Space>
                }>
                    <Table rowKey="id" columns={[
                        { title: "Item", dataIndex: "itemName" },
                        { title: "Description", dataIndex: "description" },
                        { title: "Qty", render: (col, record) => <Number value={record.quantity} /> },
                        { title: "U/M", dataIndex: "uom" },
                        { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                        { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
                    ]} data={bill.items} />
                </TabPane>
            </Tabs>
        </Card >
    )
}
